import { DOCUMENT } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { ConsentBanner, ConsentService } from './consent';
import { SiteMenu } from './site-menu';
import { AppUpdateService, UpdateBanner } from './update-banner';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, SiteMenu, ConsentBanner, UpdateBanner],
  templateUrl: './app.html',
  styles: `
    .site-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: var(--fai-header-height);
      padding: 0 1rem;
      border-bottom: 1px solid var(--fai-color-border);
      background: var(--fai-color-steel-100);
    }
    .logo {
      font-weight: 700;
      font-size: 1.2rem;
      color: var(--fai-color-primary);
    }
  `,
})
export class App {
  router = inject(Router);
  title = inject(Title);
  meta = inject(Meta);
  document = inject(DOCUMENT);

  constructor() {
    inject(ConsentService).init();
    inject(AppUpdateService);
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((n: NavigationEnd) => {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) route = route.firstChild;
        const pageTitle = route.data['title'];
        if (pageTitle) {
          this.title.setTitle(`${pageTitle} · Friendlai`);
        } else if (pageTitle !== false) {
          this.title.setTitle('Friendlai');
        }
        const url = 'https://friendlai.xyz' + n.urlAfterRedirects.split('?')[0];
        this.setCanonicalUrl(url);
        // Link previews read these from server-rendered and prerendered pages.
        this.meta.updateTag({ property: 'og:title', content: this.title.getTitle() });
        this.meta.updateTag({ property: 'og:url', content: url });
        // Shared results are people's own content: crawlable for link previews
        // (Twitterbot obeys robots.txt), but kept out of search results.
        if (route.data['noindex']) this.meta.updateTag({ name: 'robots', content: 'noindex' });
        else this.meta.removeTag('name="robots"');
      });
  }

  setCanonicalUrl(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
